import { getAttributionPOIsInfo } from '../multiFlightMap.resources';
import { textCell } from './mappers/mapper.helpers';

import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { PoiInfo } from './mappers/venues.mapper';

type SourceRequest = { country: string; geoDataIds: string[]; sourceId: string };

const groupBySource = (venues: Partial<Venue>[]): SourceRequest[] => {
  const requests = new Map<string, SourceRequest>();
  for (const { country, geoDataId, geoDataSourceId } of venues) {
    if (!geoDataId || !geoDataSourceId || !country) {
      continue;
    }
    const key = `${geoDataSourceId}|${country}`;
    const request = requests.get(key) ?? { country, geoDataIds: [], sourceId: geoDataSourceId };
    request.geoDataIds.push(geoDataId);
    requests.set(key, request);
  }
  return [...requests.values()];
};

/** The endpoint returns the POI `id` as a number, while the venues store their `geoDataId` as a string. */
const idCell = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const fetchSource = async ({ country, geoDataIds, sourceId }: SourceRequest): Promise<Record<string, PoiInfo>> => {
  const { items } = await getAttributionPOIsInfo(sourceId, country, geoDataIds);
  const info: Record<string, PoiInfo> = {};
  // The endpoint is typed as screens, but returns POIs with the raw source fields (`brand`, `tier1_category`)
  for (const item of items as unknown as Record<string, unknown>[]) {
    const id = idCell(item.geoDataId) || idCell(item.id);
    if (id) {
      info[id] = { brand: textCell(item.brand), category: textCell(item.tier1_category) || textCell(item.category) };
    }
  }
  return info;
};

/**
 * Brand and category of the venues' POIs by `geoDataId` (the venues themselves do not store them). Venues without
 * source data are skipped and a failing source is ignored: its POIs just export empty Brand/Category cells.
 */
export const fetchPoiInfo = async (venues: Partial<Venue>[]): Promise<Record<string, PoiInfo>> => {
  const results = await Promise.allSettled(groupBySource(venues).map(fetchSource));
  return Object.assign({}, ...results.map(result => (result.status === 'fulfilled' ? result.value : {})));
};
